import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import type { WeatherState } from '../sim/Weather';
import type { ShadowConfig } from '../ui/ShadowStore';

const HDRI_URL = '/assets/hdri/kloofendal_48d_partly_cloudy_puresky_2k.hdr';

export class World {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  sun: THREE.DirectionalLight;
  private hemi: THREE.HemisphereLight;
  private resizeCallbacks: Array<(w: number, h: number) => void> = [];

  constructor(mount: HTMLElement) {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    // Cap DPR lower on mobile — iPhones at DPR 3 burn a lot of GPU for a
    // scene this busy and Safari was killing the context.
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    // Cap pixel ratio lower than physical density — 2× DPR on a retina display
    // quadruples the pixel count for little perceptual win. 1.5 stays crisp.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.25 : 1.5));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;
    mount.appendChild(this.renderer.domElement);

    // iOS Safari can drop the WebGL context under memory pressure; log it
    // loudly but prevent the browser's default auto-reload behaviour.
    this.renderer.domElement.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('WebGL context lost');
    });
    this.renderer.domElement.addEventListener('webglcontextrestored', () => {
      console.info('WebGL context restored');
    });

    this.scene = new THREE.Scene();

    // Sun — strong directional light. Intensity pushed high so the cast shadow
    // is actually readable against the bright HDRI ambient.
    this.sun = new THREE.DirectionalLight(0xfff2d6, 6.0);
    this.sun.position.set(35, 55, 20);
    this.sun.castShadow = true;
    // Shadow map: 1 k. 2 k was a single biggest GPU frame chunk; with the
    // tight ±6 m follow frustum, 1 k still gives ~12 mm per texel which
    // reads fine at ant scale and ~halves the shadow-pass cost.
    const shadowSize = 1024;
    this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    // Tight ortho frustum centred on the camera target. At 4096² this lands
    // ~3 mm per texel so individual ant body segments actually cast readable
    // shadows. The shadow camera follows the orbit target each frame so the
    // tight bounds travel with the player.
    const s = 6;
    this.sun.shadow.camera.left = -s;
    this.sun.shadow.camera.right = s;
    this.sun.shadow.camera.top = s;
    this.sun.shadow.camera.bottom = -s;
    this.sun.shadow.camera.near = 1;
    this.sun.shadow.camera.far = 200;
    this.sun.shadow.camera.updateProjectionMatrix();
    // normalBias was 0.04 metres — ~4 cm in world units. The shadowmap sample
    // position gets pushed that far along the surface normal before sampling
    // the depth map, which is LARGER than the entire visual length of an ant
    // (~1 cm). Tiny casters get Peter-Panned off the receiver entirely, which
    // is why "no shadows" appeared even though everything was flagged
    // correctly. Drop the offset so ant-scale shadows survive, and lean on a
    // stronger depth bias plus the ortho frustum/near-plane to handle acne on
    // the large terrain receiver.
    this.sun.shadow.bias = -0.0003;
    // normalBias was pushing samples 5 mm along the surface normal — half an
    // ant body length. Drop to 1 mm so ant shadows aren't Peter-Panned into
    // the terrain mesh instead of onto it.
    this.sun.shadow.normalBias = 0.001;
    this.sun.shadow.radius = 3;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);

    // Soft sky fill — low so the sun contrast (and its shadows) dominate.
    this.hemi = new THREE.HemisphereLight(0xaad8ff, 0x4a3b2a, 0.15);
    this.scene.add(this.hemi);

    window.addEventListener('resize', this.handleResize);
  }

  async loadEnvironment() {
    try {
      const hdr = await new RGBELoader().loadAsync(HDRI_URL);
      hdr.mapping = THREE.EquirectangularReflectionMapping;
      this.scene.environment = hdr;
      this.scene.background = hdr;
      this.scene.backgroundBlurriness = 0.0;
      this.scene.backgroundIntensity = 1.0;
      // Lower the IBL ambient so shadows from the sun remain visible.
      // (r155+ supports scene.environmentIntensity.)
      (this.scene as THREE.Scene & { environmentIntensity?: number }).environmentIntensity = 0.5;
    } catch (e) {
      console.warn('HDRI missing, using gradient sky fallback', e);
      this.scene.background = new THREE.Color(0x9fc5e8);
    }
    // Subtle atmospheric haze. Keep density low so distant hills keep their
    // colour; the "blurry distance" look is produced by the DoF post-effect,
    // not by fogging everything to sky colour.
    this.scene.fog = new THREE.FogExp2(0xc5d8e8, 0.015);
  }

  onResize(cb: (w: number, h: number) => void) {
    this.resizeCallbacks.push(cb);
  }

  private handleResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    for (const cb of this.resizeCallbacks) cb(w, h);
  };

  /**
   * Apply the user-tunable shadow settings from /editor. Shadow radius / bias
   * / exposure persist because Weather doesn't touch them each frame. Sun
   * intensity stays Weather-driven (time of day) but is multiplied by the
   * tuned `sunIntensity` relative to its server-default of 6.0.
   */
  private sunIntensityBase = 6.0; // Weather's peak-noon output
  applyShadowConfig(cfg: ShadowConfig): void {
    this.sun.shadow.radius = cfg.shadowRadius;
    this.sun.shadow.bias = cfg.shadowBias;
    this.renderer.toneMappingExposure = cfg.exposure;
    this.hemi.intensity = cfg.ambientIntensity;
    // Remember the tuned peak intensity so applyWeather can scale to it.
    this.sunIntensityBase = cfg.sunIntensity;
  }

  applyWeather(state: WeatherState, distance = 60, focus?: { x: number; z: number }): void {
    // Follow the camera target with the shadow frustum so the tight ortho
    // bounds stay centred on where the player is looking.
    const fx = focus?.x ?? 0;
    const fz = focus?.z ?? 0;
    this.sun.target.position.set(fx, 0, fz);
    this.sun.target.updateMatrixWorld();
    // Position the sun light so it SHINES from the sun toward the focus.
    // state.sunDir points FROM sun TO target, so the sun itself sits at
    // (focus - sunDir * distance).
    this.sun.position.copy(state.sunDir).multiplyScalar(-distance).add(this.sun.target.position);
    this.sun.color.copy(state.sunColor);
    // Weather's sunIntensity peaks at 6.0; scale to the tuned peak base.
    this.sun.intensity = state.sunIntensity * (this.sunIntensityBase / 6.0);

    this.hemi.intensity = state.ambientIntensity;

    const fog = this.scene.fog;
    if (fog instanceof THREE.FogExp2) {
      fog.color.copy(state.fogColor);
      fog.density = state.fogDensity;
    }

    (this.scene as THREE.Scene & { environmentIntensity?: number }).environmentIntensity =
      state.environmentIntensity;

    // HDRI is still bright at night; dim the background to let the night sky read.
    if (state.sunIntensity < 0.3) {
      this.scene.backgroundIntensity = 0.2 + (state.sunIntensity / 0.3) * 0.8;
    } else {
      this.scene.backgroundIntensity = 1.0;
    }
  }
}
